import type { FC } from 'react';

import { useBooleanFlags } from '@/core/flags/flags.hooks';
import { Typography } from '@/core/tamagoshiTailwind/components/Typography/Typography';
import { ManoExpressMLogo } from '@/domains/core/tamagoshiTailwind/logos/manoExpress/ManoExpressMLogo';
import { SuperProMulticolorDarkSLogo } from '@/domains/core/tamagoshiTailwind/logos/superPro/SuperProMulticolorDarkSLogo';
import { B2B_SUBSCRIPTIONS_MVP_FEATURE_FLAG } from '@/domains/loyalty/modules/Subscriptions/constants';
import { useUserHasSubscription } from '@/domains/loyalty/modules/Subscriptions/hooks/useUserHasSubscription';
import { DeliveryOffer } from '@/productDiscovery/ProductPage/common/modules/Delivery/delivery.ducks';
import { useIsProductEligibleForSubscriptionDiscount } from '@/productDiscovery/ProductPage/common/modules/Delivery/hooks/useIsProductEligibleForSubscriptionDiscount';
import { deliveryWith } from '@/productDiscovery/ProductPage/common/modules/Delivery/translations';
import { getDeliveryWording } from '@/productDiscovery/ProductPage/common/modules/Delivery/Utils/getDeliveryWording';
import { useDetail } from '@/productDiscovery/ProductPage/hooks/useDetail';

import { DeliveryPrice } from './DeliveryPrice/DeliveryPrice';

interface Props {
  offer: DeliveryOffer;
}

export const DeliveryWordingRevamp: FC<Props> = ({ offer }) => {
  const { deliveryWordingMode, deliveryWording } = getDeliveryWording(offer);
  const { delivery } = useDetail();
  const [isSubscriptionsMvpEnabled] = useBooleanFlags([
    B2B_SUBSCRIPTIONS_MVP_FEATURE_FLAG,
  ]);
  const isUserSubscribed = useUserHasSubscription();
  const isProductEligibleForSubscription =
    useIsProductEligibleForSubscriptionDiscount(offer);

  const shouldDisplaySubscription =
    isSubscriptionsMvpEnabled &&
    isUserSubscribed &&
    isProductEligibleForSubscription;

  const price = offer.deliveryPrice.amount;
  const isFreeDelivery = price === 0;

  return (
    <>
      <div className="flex items-center gap-xs">
        <Typography scale="body2">{deliveryWordingMode} -</Typography>
        {shouldDisplaySubscription ? (
          <div className="flex items-center gap-xs">
            {delivery?.fulfillment && (
              <ManoExpressMLogo data-testid="manomanoExpressLogo" />
            )}
            {!isFreeDelivery && <DeliveryPrice value={price} obsolete />}
            <DeliveryPrice value={price} isSubscription={true} />
            <Typography scale="body2">{deliveryWith}</Typography>
            <SuperProMulticolorDarkSLogo
              data-testid="super-pro"
              className="h-[13px] w-[65px] align-baseline"
            />
          </div>
        ) : (
          <DeliveryPrice value={price} />
        )}
        {delivery?.fulfillment && !shouldDisplaySubscription && (
          <ManoExpressMLogo data-testid="manomanoExpressLogo" />
        )}
      </div>
      <div>
        <Typography scale="body2" className="text-foundation-secondary">
          {deliveryWording}
        </Typography>
      </div>
    </>
  );
};
